import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { empresaService } from "@services/api";
import { useAuth } from "@contexts/AuthContext";

const STORAGE_KEY = "empresaAtivaId";

const EmpresaContext = createContext({});

// eslint-disable-next-line react-refresh/only-export-components
export const useEmpresa = () => {
  const context = useContext(EmpresaContext);
  if (!context) {
    throw new Error("useEmpresa deve ser usado dentro de um EmpresaProvider");
  }
  return context;
};

export const EmpresaProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const [empresas, setEmpresas] = useState([]);
  const [empresaAtivaId, setEmpresaAtivaId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  // Falso até a PRIMEIRA carga das empresas terminar (com sucesso ou erro).
  const [carregado, setCarregado] = useState(false);

  // Persistir o id da empresa ativa no localStorage (lido pelo interceptor da API).
  const persistEmpresaAtiva = useCallback((id) => {
    try {
      if (id) {
        localStorage.setItem(STORAGE_KEY, String(id));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // ignora erros de storage
    }
  }, []);

  // Carrega as empresas do usuário autenticado e ajusta a empresa ativa.
  const recarregarEmpresas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await empresaService.getAll();
      const lista = response.data?.data || [];
      setEmpresas(lista);

      setEmpresaAtivaId((prev) => {
        const aindaExiste =
          prev != null &&
          lista.some((empresa) => String(empresa.id) === String(prev));

        // Mantém a seleção salva se ainda for válida; senão usa a primeira.
        const proximaId = aindaExiste
          ? prev
          : lista.length > 0
            ? String(lista[0].id)
            : null;

        persistEmpresaAtiva(proximaId);
        return proximaId;
      });

      return lista;
    } catch (error) {
      console.error("Erro ao carregar empresas:", error);
      setEmpresas([]);
      return [];
    } finally {
      setLoading(false);
      setCarregado(true);
    }
  }, [persistEmpresaAtiva]);

  // Carrega as empresas quando o usuário está autenticado; limpa ao deslogar.
  useEffect(() => {
    if (isAuthenticated) {
      recarregarEmpresas();
    } else {
      setEmpresas([]);
      setEmpresaAtivaId(null);
      persistEmpresaAtiva(null);
      setCarregado(false);
    }
  }, [isAuthenticated, recarregarEmpresas, persistEmpresaAtiva]);

  // Seleciona a empresa ativa e invalida apenas caches dependentes do escopo.
  const selecionarEmpresa = useCallback(
    (id) => {
      const novoId = id != null ? String(id) : null;
      setEmpresaAtivaId(novoId);
      persistEmpresaAtiva(novoId);
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [rootKey] = query.queryKey;
          return !["auth", "me", "health"].includes(rootKey);
        },
      });
    },
    [persistEmpresaAtiva, queryClient],
  );

  const empresaAtiva =
    empresas.find((empresa) => String(empresa.id) === String(empresaAtivaId)) ||
    null;

  const value = {
    empresas,
    empresaAtiva,
    empresaAtivaId,
    papelNaEmpresaAtiva: empresaAtiva?.papel || null,
    // Autenticado e ainda sem a primeira carga: já é "carregando" na própria
    // primeira renderização (o efeito que dispara a carga só roda depois dela);
    // sem isso os guards liam papel nulo e redirecionavam para a tela errada.
    loading: loading || (isAuthenticated && !carregado),
    selecionarEmpresa,
    recarregarEmpresas,
  };

  return (
    <EmpresaContext.Provider value={value}>{children}</EmpresaContext.Provider>
  );
};

export default EmpresaContext;
